import React, { useEffect, useState } from "react";
import { guardarEstudio, obtenerEstudio } from "../api";
import { toast } from "react-toastify";
import { HiSave, HiX, HiOutlineCalculator } from "react-icons/hi";
import "../styles.css";

const categorias = [
  { titulo: "C.D. Presupuestado", campos: ["coste_directo_presupuestado"] },
  { titulo: "Modificaciones del estudio", campos: ["modificaciones_estudio"] },
  { titulo: "C.D. Riesgos", campos: ["coste_directo_con_riesgos"] },
  {
    titulo: "C.I. Fijos",
    campos: [
      "mo_indirecta",
      "desplazamientos_dietas",
      "oficinas_instalaciones",
      "maquinaria_medios",
      "herramientas_material",
      "otros_gastos",
    ],
    totalKey: "total_costes_indirectos_fijos",
  },
  { titulo: "Total E.M.", campos: ["total_ejecucion_material"] },
  { titulo: "Total E.M. Riesgos", campos: ["total_ejecucion_material_riesgos"] },
];

const etiquetas = {
  coste_directo_presupuestado: "a.- Coste directo presupuestado según estudio (sin IVA)",
  modificaciones_estudio: "b.- Modificaciones del estudio",
  coste_directo_con_riesgos: "c.- Coste directo restando los riesgos de estudio (sin IVA)",
  mo_indirecta: "d.1.- M.O. indirecta",
  desplazamientos_dietas: "d.2.- Desplazamientos y dietas",
  oficinas_instalaciones: "d.3.- Oficinas e instalaciones",
  maquinaria_medios: "d.4.- Maquinaria y medios auxiliares",
  herramientas_material: "d.5.- Herramientas y pequeño material",
  otros_gastos: "d.6.- Otros Gastos",
  total_costes_indirectos_fijos: "TOTAL COSTES INDIRECTOS FIJOS (d)",
  total_ejecucion_material: "TOTAL EJECUCIÓN MATERIAL (a+d)",
  total_ejecucion_material_riesgos: "TOTAL EJECUCIÓN MATERIAL CON RIESGOS DE ESTUDIO (c+d)",
};

const Costes = ({ numeroExpediente, onCerrar }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const cargar = async () => {
      try {
        const estudio = await obtenerEstudio(numeroExpediente, "costes");
        const detalle = await obtenerEstudio(numeroExpediente, "detalle_costes");

        const mergedData = { ...estudio };

        if (detalle) {
          const sumaImportes = (campos) =>
            campos.reduce((acc, k) => {
              const val = detalle[k]?.importe || 0;
              return acc + parseFloat(val);
            }, 0);

          const moaKeys = [
            "Gerente", "Jefe de Obra", "Jefe de Producción o Ayte. de Obra",
            "Jefe de Oficina Técnica", "Topógrafo", "Encargado", "Administrativo",
            "Vigilante", "Calidad", "Pilotos RENFE", "Auxiliares Administrativos",
            "Ayudantes Topografía", "Auxiliares Control de Calidad", "Primas de Producción"
          ];
          mergedData.mo_indirecta = sumaImportes(moaKeys);
          mergedData.total_mo_directa_tareas_indirectas = detalle["Mano de obra directa en tareas indirectas"]?.importe || 0;
          mergedData.total_d1 = mergedData.mo_indirecta + mergedData.total_mo_directa_tareas_indirectas;

          mergedData.total_dietas_directo = sumaImportes(["Dietas", "Viajes"]);
          mergedData.total_mo_indirecta_dietas = sumaImportes(["Vehículos", "Dietas PI", "Viajes PI"]);
          mergedData.desplazamientos_dietas = mergedData.total_dietas_directo + mergedData.total_mo_indirecta_dietas;
          mergedData.total_d2 = mergedData.desplazamientos_dietas;

          mergedData.oficinas_instalaciones = sumaImportes([
            "Oficinas, vestuarios y almacenes",
            "Servicios (agua, gas electricidad, teléfono)",
            "Acometidas"
          ]);
          mergedData.total_d3 = mergedData.oficinas_instalaciones;

          mergedData.maquinaria_medios = sumaImportes(["Maquinaria Ligera", "Transporte", "Medidas auxiliares"]);
          mergedData.total_d4 = mergedData.maquinaria_medios;

          mergedData.herramientas_material = sumaImportes([
            "Materiales auxiliares",
            "Herramientas",
            "Señalización y elementos de seguridad",
            "Material de oficina"
          ]);
          mergedData.total_d5 = mergedData.herramientas_material;

          mergedData.otros_gastos = sumaImportes(["Anuncios", "Carteles", "Seguros", "Divulgación Informativa", "Publicidad Obra"]);
          mergedData.total_d6 = mergedData.otros_gastos;

          mergedData.total_costes_indirectos_fijos = mergedData.total_d1 + mergedData.total_d2 + mergedData.total_d3 + mergedData.total_d4 + mergedData.total_d5 + mergedData.total_d6;
        }

        const a = parseFloat(mergedData.coste_directo_presupuestado) || 0;
        const c = parseFloat(mergedData.coste_directo_con_riesgos) || 0;
        mergedData.total_ejecucion_material = a + (mergedData.total_costes_indirectos_fijos || 0);
        mergedData.total_ejecucion_material_riesgos = c + (mergedData.total_costes_indirectos_fijos || 0);

        setData(mergedData);
      } catch (err) {
        console.warn("Sin datos previos", err);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [numeroExpediente]);

  const handleGuardar = async () => {
    try {
      await guardarEstudio(numeroExpediente, "costes", data);
      toast.success("Estudio de costes guardado");
    } catch {
      toast.error("Error al guardar estudio");
    }
  };

  const renderCampo = (key, readOnly = false) => (
    <div key={key} className="form-group" style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{etiquetas[key]}</label>
      <div style={{ position: 'relative' }}>
        <input
          type="number"
          step="any"
          value={data[key] || ""}
          onChange={(e) => !readOnly && setData({ ...data, [key]: parseFloat(e.target.value) || 0 })}
          readOnly={readOnly}
          style={{
            width: '100%',
            padding: '12px',
            background: readOnly ? 'rgba(255,255,255,0.03)' : 'var(--bg-main)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            color: readOnly ? 'var(--accent-primary)' : 'var(--text-primary)',
            fontWeight: readOnly ? 700 : 400,
            cursor: readOnly ? 'default' : 'text'
          }}
        />
        {readOnly && <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.8rem', color: 'var(--accent-primary)', opacity: 0.6 }}>AUTO</div>}
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
      <div className="loader"></div>
    </div>
  );

  return (
    <div className="glass-panel animate-fadeIn" style={{ padding: '32px', borderRadius: 'var(--radius-lg)', maxWidth: '1000px', margin: '0 auto' }}>
      <div className="dashboard-header" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="brand-logo" style={{ width: '48px', height: '48px', fontSize: '1.4rem' }}>
            <HiOutlineCalculator />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Estudio de Costes</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Análisis técnico-económico del proyecto</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-gestion" onClick={onCerrar} style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)' }}>
            <HiX /> Cerrar
          </button>
          <button className="btn-nueva" onClick={handleGuardar}>
            <HiSave /> Guardar Estudio
          </button>
        </div>
      </div>

      <div className="tabs-container" style={{ display: 'flex', gap: '8px', marginBottom: '32px', overflowX: 'auto', paddingBottom: '8px', borderBottom: '1px solid var(--glass-border)' }}>
        {categorias.map((cat, idx) => (
          <button
            key={cat.titulo}
            onClick={() => setActiveTab(idx)}
            style={{
              padding: '10px 20px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: idx === activeTab ? 'rgba(56, 189, 248, 0.1)' : 'transparent',
              color: idx === activeTab ? 'var(--accent-primary)' : 'var(--text-secondary)',
              fontWeight: idx === activeTab ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'var(--transition-fast)',
              borderBottom: idx === activeTab ? '2px solid var(--accent-primary)' : '2px solid transparent'
            }}
          >
            {cat.titulo}
          </button>
        ))}
      </div>

      <div className="tab-content animate-fadeIn">
        <h3 style={{ fontSize: '1.1rem', marginBottom: '24px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '4px', height: '1.2rem', background: 'var(--accent-primary)', borderRadius: '2px' }}></span>
          {categorias[activeTab].titulo}
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {categorias[activeTab].campos.map((key) => renderCampo(key, activeTab === 3 || key === categorias[activeTab].totalKey))}
          {categorias[activeTab].totalKey && renderCampo(categorias[activeTab].totalKey, true)}
        </div>
      </div>
    </div>
  );
};

export default Costes;
